import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPackages, deletePackage, unarchivePackage } from '../lib/api';
import { formatDate, formatStatus, getStatusClass, getDirectionClass } from '../lib/utils';
import { getCarrierColor, getCarrierInitials } from '../lib/carrierLogos';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, ArrowLeft, Undo2, Trash2, ArrowDownLeft, ArrowUpRight, Loader2, Clock, Moon, Sun, Search, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

export default function ArchivePage() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('date_desc');
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();

    const fetchData = useCallback(async () => {
        try {
            const pkgs = await getPackages(true);
            setPackages(pkgs);
        } catch (error) {
            toast.error('Failed to load archived packages');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = async (id) => {
        try {
            await deletePackage(id);
            setPackages(packages.filter(p => p.id !== id));
            toast.success('Package deleted');
        } catch (error) {
            toast.error('Failed to delete package');
        }
    };

    const handleUnarchive = async (id) => {
        try {
            await unarchivePackage(id);
            setPackages(packages.filter(p => p.id !== id));
            toast.success('Package restored to dashboard');
        } catch (error) {
            toast.error('Failed to restore package');
        }
    };

    // Filter packages by search query
    const filteredPackages = packages.filter(pkg => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            pkg.tracking_number.toLowerCase().includes(query) ||
            (pkg.description && pkg.description.toLowerCase().includes(query))
        );
    });

    // Sort packages
    const sortedPackages = [...filteredPackages].sort((a, b) => {
        switch (sortBy) {
            case 'date_asc':
                return new Date(a.created_at) - new Date(b.created_at);
            case 'date_desc':
                return new Date(b.created_at) - new Date(a.created_at);
            case 'status_asc':
                return (a.status || '').localeCompare(b.status || '');
            case 'status_desc':
                return (b.status || '').localeCompare(a.status || '');
            default:
                return 0;
        }
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="archive-loading">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="min-h-screen noise-bg" data-testid="archive-page">
            {/* Navbar */}
            <header className="sticky top-0 z-50 backdrop-blur-md bg-background/80 border-b border-border">
                <div className="flex justify-between items-center h-16 px-6 max-w-[1400px] mx-auto">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-zinc-900 dark:bg-zinc-100 rounded-sm">
                            <Package className="h-5 w-5 text-zinc-100 dark:text-zinc-900" strokeWidth={1.5} />
                        </div>
                        <span className="text-lg font-bold tracking-tight">Tracker</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Theme Toggle */}
                        <Button
                            variant="outline"
                            size="icon"
                            onClick={toggleTheme}
                            className="rounded-sm"
                            data-testid="theme-toggle"
                        >
                            {theme === 'light' ? (
                                <Moon className="h-4 w-4" strokeWidth={1.5} />
                            ) : (
                                <Sun className="h-4 w-4" strokeWidth={1.5} />
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/')}
                            className="rounded-sm"
                            data-testid="back-to-dashboard"
                        >
                            <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="p-6 md:p-12 max-w-[1400px] mx-auto">
                <div className="mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Archive</h1>
                    <p className="text-muted-foreground text-sm">
                        {packages.length} delivered package{packages.length !== 1 ? 's' : ''}
                    </p>
                </div>

                {/* Search and Sort Controls */}
                {packages.length > 0 && (
                    <div className="flex flex-col sm:flex-row gap-4 mb-6">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search tracking # or description..."
                                className="pl-10 rounded-sm"
                                data-testid="archive-search-input"
                            />
                        </div>
                        <Select value={sortBy} onValueChange={setSortBy}>
                            <SelectTrigger className="w-[200px] rounded-sm" data-testid="archive-sort-select">
                                <ArrowUpDown className="h-4 w-4 mr-2" strokeWidth={1.5} />
                                <SelectValue placeholder="Sort by" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="date_desc">Newest First</SelectItem>
                                <SelectItem value="date_asc">Oldest First</SelectItem>
                                <SelectItem value="status_asc">Status A-Z</SelectItem>
                                <SelectItem value="status_desc">Status Z-A</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                )}

                {sortedPackages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="archive-empty">
                        <div className="relative w-48 h-48 mb-6">
                            <img
                                src="https://images.unsplash.com/photo-1573376671096-e1fce2d1f19d?crop=entropy&cs=srgb&fm=jpg&q=85&w=400"
                                alt="Empty archive"
                                className="w-full h-full object-cover rounded-sm empty-state-image"
                            />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">
                            {searchQuery ? 'No matching packages' : 'No archived packages'}
                        </h3>
                        <p className="text-muted-foreground text-sm max-w-sm">
                            {searchQuery 
                                ? 'Try adjusting your search query to find what you\'re looking for.'
                                : 'Delivered packages will appear here. Packages are automatically archived when marked as delivered.'}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="archive-grid">
                        {sortedPackages.map((pkg, index) => (
                            <Card
                                key={pkg.id}
                                className="border border-border rounded-sm shadow-none animate-slide-up"
                                style={{ animationDelay: `${index * 50}ms` }}
                                data-testid={`archive-card-${pkg.id}`}
                            >
                                <CardHeader className="p-4 pb-2">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-center gap-2">
                                            <Badge className={`${getDirectionClass(pkg.direction)} rounded-full text-xs font-semibold`}>
                                                {pkg.direction === 'inbound' ? (
                                                    <ArrowDownLeft className="h-3 w-3 mr-1" />
                                                ) : (
                                                    <ArrowUpRight className="h-3 w-3 mr-1" />
                                                )}
                                                {pkg.direction}
                                            </Badge>
                                            <Badge className={`${getStatusClass(pkg.status)} rounded-full text-xs font-semibold`}>
                                                {formatStatus(pkg.status)}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 rounded-sm"
                                                onClick={() => handleUnarchive(pkg.id)}
                                                data-testid={`unarchive-btn-${pkg.id}`}
                                            >
                                                <Undo2 className="h-4 w-4" strokeWidth={1.5} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 rounded-sm text-destructive hover:text-destructive"
                                                onClick={() => handleDelete(pkg.id)}
                                                data-testid={`delete-btn-${pkg.id}`}
                                            >
                                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                            </Button>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-4 pt-2 space-y-3">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Tracking #</p>
                                        <p className="font-mono font-medium tracking-wide text-sm">
                                            {pkg.tracking_number}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4 text-sm">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <CarrierLogo carrier={pkg.carrier} carrierName={pkg.carrier_name} size="md" />
                                            <span>{pkg.carrier_name}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-muted-foreground">
                                            <Clock className="h-4 w-4" strokeWidth={1.5} />
                                            <span>{formatDate(pkg.updated_at).split(',')[0]}</span>
                                        </div>
                                    </div>

                                    {pkg.description && (
                                        <p className="text-sm text-muted-foreground italic">"{pkg.description}"</p>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

// Carrier Logo Component
function CarrierLogo({ carrier, carrierName, size = "md" }) {
    const color = getCarrierColor(carrier);
    const initials = getCarrierInitials(carrierName);
    
    const sizeClasses = {
        sm: "h-5 w-5 text-[8px]",
        md: "h-8 w-8 text-xs",
        lg: "h-10 w-10 text-sm"
    };

    return (
        <div 
            className={`${sizeClasses[size]} rounded-sm flex items-center justify-center font-bold text-white flex-shrink-0`}
            style={{ backgroundColor: color }}
            title={carrierName}
        >
            {initials}
        </div>
    );
}
